import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Loader2, Save, Sparkles, Users, RefreshCw, AlertTriangle, Wand2, FileText, Check } from 'lucide-react';
import { toast } from 'sonner';
import { serviceDeliveryAPI } from '../lib/api';
import { BRIEFING_SECTIONS } from '../lib/briefingConfig';

const AVATAR = BRIEFING_SECTIONS.find((s) => s.key === 'avatar');
// Group the avatar fields by their 'info' headers (_g1.._g5)
const GROUPS = [];
if (AVATAR) {
  let cur = null;
  for (const f of AVATAR.fields) {
    if (f.type === 'info') { cur = { title: f.label, fields: [] }; GROUPS.push(cur); }
    else if (cur) cur.fields.push(f);
  }
}

export default function BuyerPersonaSection({ clientId }) {
  const [form, setForm] = useState(null);
  const [briefing, setBriefing] = useState({});
  const [autoFields, setAutoFields] = useState([]);
  const [changed, setChanged] = useState([]);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const initialAutoRef = useRef(new Set());
  const editedRef = useRef(new Set());
  const [extracting, setExtracting] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [sourceFile, setSourceFile] = useState('');
  const [selProp, setSelProp] = useState(new Set());
  const [proposalOpen, setProposalOpen] = useState(false);

  const labelFor = (k) => (AVATAR?.fields.find((f) => f.key === k) || {}).label || k;

  const handleExtract = async () => {
    setExtracting(true);
    try {
      const res = await serviceDeliveryAPI.extractBuyerPersona(clientId);
      setProposal(res.proposal || {});
      setSourceFile(res.source_file || '');
      setSelProp(new Set(res.found_fields || []));
      setProposalOpen(true);
    } catch (e) {
      toast.error(e?.message || 'No se pudo extraer del plan');
    } finally {
      setExtracting(false);
    }
  };

  const toggleProp = (k) => setSelProp((prev) => {
    const n = new Set(prev);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });

  const applyProposal = () => {
    let n = 0;
    selProp.forEach((k) => { if (proposal[k]) { setVal(k, proposal[k]); n += 1; } });
    setProposalOpen(false);
    toast.success(`${n} ${n === 1 ? 'campo aplicado' : 'campos aplicados'}. Revisa y pulsa Guardar.`);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await serviceDeliveryAPI.getBuyerPersona(clientId);
      setForm(res?.data || {});
      setBriefing(res?.briefing || {});
      const auto = Array.isArray(res?.auto_fields) ? res.auto_fields : [];
      setAutoFields(auto);
      setChanged(Array.isArray(res?.changed_fields) ? res.changed_fields : []);
      setSaved(!!res?.saved);
      initialAutoRef.current = new Set(auto);
      editedRef.current = new Set();
    } catch (e) {
      toast.error('Error cargando el buyer persona');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const setVal = (key, v) => {
    setForm((prev) => ({ ...prev, [key]: v }));
    editedRef.current.add(key);
    setAutoFields((prev) => prev.filter((f) => f !== key));
    setChanged((prev) => prev.filter((f) => f !== key));
  };

  const applyBriefing = (key) => setVal(key, briefing[key] || '');
  const applyAllChanges = () => { changed.forEach((k) => applyBriefing(k)); toast.success('Cambios del briefing aplicados. Recuerda guardar.'); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.keys(form).forEach((f) => {
        const persist = editedRef.current.has(f) || !initialAutoRef.current.has(f);
        payload[f] = persist ? (form[f] || '') : '';
      });
      await serviceDeliveryAPI.saveBuyerPersona(clientId, payload);
      toast.success('Buyer persona guardado');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }

  const isTextarea = (key) => (AVATAR.fields.find((f) => f.key === key) || {}).type === 'textarea';

  return (
    <Card data-testid="buyer-persona-section">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> Buyer Persona</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Se rellena automáticamente desde el briefing del cliente (sección "Cliente ideal"). Puedes editar o completar cualquier campo a mano.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleExtract} disabled={extracting} data-testid="buyer-persona-extract">
              {extracting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              Extraer del plan
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="buyer-persona-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {!saved && autoFields.length > 0 && (
          <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-sm text-violet-800 flex items-start gap-2" data-testid="buyer-persona-autofill-banner">
            <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Rellenado automáticamente desde el briefing del cliente. Revisa, edita lo que necesites y pulsa <strong>Guardar</strong> para fijarlo.</span>
          </div>
        )}

        {changed.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="buyer-persona-changes-banner">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
              <div className="flex-1">
                <p className="text-sm text-amber-800"><strong>El cliente ha actualizado el briefing</strong> en {changed.length} {changed.length === 1 ? 'campo' : 'campos'}. Revisa cada cambio y aplícalo si quieres (no se sobrescribe nada sin tu confirmación).</p>
                <Button variant="outline" size="sm" className="mt-2 border-amber-400 text-amber-800" onClick={applyAllChanges} data-testid="buyer-persona-apply-all">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Aplicar todos los cambios
                </Button>
              </div>
            </div>
          </div>
        )}

        {GROUPS.map((g) => (
          <div key={g.title}>
            <h4 className="text-sm font-semibold text-primary uppercase tracking-wide mb-3">{g.title}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {g.fields.map((f) => {
                const isChanged = changed.includes(f.key);
                const full = isTextarea(f.key);
                return (
                  <div key={f.key} className={full ? 'md:col-span-2' : ''}>
                    <Label className="flex items-center flex-wrap gap-1">
                      {f.label}
                      {autoFields.includes(f.key) && (
                        <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px]" data-testid={`buyer-auto-${f.key}`}>
                          <Sparkles className="w-2.5 h-2.5 mr-1" /> Del briefing
                        </Badge>
                      )}
                      {isChanged && (
                        <button type="button" onClick={() => applyBriefing(f.key)} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 hover:bg-amber-200" data-testid={`buyer-apply-${f.key}`}>
                          <RefreshCw className="w-2.5 h-2.5" /> Actualizado — aplicar
                        </button>
                      )}
                    </Label>
                    {isChanged && (
                      <p className="text-[11px] text-amber-700 mt-0.5 mb-1">Nuevo en briefing: <span className="italic">{(briefing[f.key] || '').slice(0, 140)}</span></p>
                    )}
                    {full ? (
                      <Textarea value={form[f.key] || ''} onChange={(e) => setVal(f.key, e.target.value)} rows={3} className="resize-none" data-testid={`buyer-${f.key}`} />
                    ) : (
                      <Input value={form[f.key] || ''} onChange={(e) => setVal(f.key, e.target.value)} data-testid={`buyer-${f.key}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </CardContent>

      <Dialog open={proposalOpen} onOpenChange={setProposalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="buyer-proposal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wand2 className="w-5 h-5 text-primary" /> Propuesta extraída del plan</DialogTitle>
            <DialogDescription className="flex items-center gap-1">
              <FileText className="w-3.5 h-3.5" /> {sourceFile || 'Plan de Social Media'} · Marca los campos que quieras aplicar. Nada se guarda hasta que pulses "Guardar" después.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {proposal && Object.keys(proposal).filter((k) => proposal[k]).length === 0 && (
              <p className="text-sm text-muted-foreground">No se encontró información de buyer persona en el PDF.</p>
            )}
            {proposal && Object.keys(proposal).filter((k) => proposal[k]).map((k) => (
              <label key={k} className="flex items-start gap-2 rounded-lg border p-3 cursor-pointer hover:border-primary/40" data-testid={`proposal-field-${k}`}>
                <input type="checkbox" className="accent-primary mt-1" checked={selProp.has(k)} onChange={() => toggleProp(k)} data-testid={`proposal-check-${k}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{labelFor(k)}</p>
                  {form[k] ? (
                    <p className="text-[11px] text-muted-foreground line-through">Actual: {form[k]}</p>
                  ) : null}
                  <p className="text-sm text-emerald-700">{proposal[k]}</p>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setProposalOpen(false)}>Descartar</Button>
            <Button onClick={applyProposal} disabled={selProp.size === 0} data-testid="proposal-apply">
              <Check className="w-4 h-4 mr-2" /> Aplicar seleccionados ({selProp.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
